// TEMPORARY: fake gates data for testing the UI with a live game.
// On with the dashboard-wide TEST_MODE (src/testMode.js), or by itself with DUMMY_ENABLED below.
// It only ever runs under `npm start` (development); production builds can never enable it.
// Remove this file (and its uses in GatesLocationController.js, gatesApi.js, LocationsController.js
// and the banner in GatesLocation.js) when done.
//
// Shares the test game (gates 2 hours, start 1 hour before page load, three past games). Each gate
// reports entrances/exits and its queue every 30 seconds up to "now"; the 60s refresh adds the new
// readings. Uses the location's real gates (names, icons, queue sensors). Each gate steps through
// the edge cases below in 12-minute windows, on top of an entry rush and an exit wave.
import { getQueuesAPI } from '../../util.js';
import {
  IS_DEV, USE_TEST_MODE, noise, testSchedule, gameContext, readingTimes, localStamp, edgeCase, edgeProgress,
} from '../../testMode';

const DUMMY_ENABLED = false;

export const USE_DUMMY_GATES_DATA = IS_DEV && (DUMMY_ENABLED || USE_TEST_MODE);

const QUEUE_CASES = ['normal', 'exact10', 'nine', 'spike', 'minimum', 'rising', 'falling', 'normal'];
const FLOW_CASES = ['normal', 'rush', 'normal', 'quiet', 'normal', 'stopped'];

// Gate character: share of the crowd (first gates busiest)
const gateWeight = (index, count) => 1.6 - (index / Math.max(1, count - 1)) * 1.0 + noise(index + 3) * 0.3;

// Everyone-in-the-stadium entries and exits per minute (before splitting across gates)
const entryRate = (ctx, m) => {
  const peak = ctx.start - 35;
  if (m < ctx.gates) return 1.5;
  if (m <= ctx.start + 10) return 8 + 95 * Math.exp(-Math.pow((m - peak) / 28, 2));
  return 6 * Math.exp(-(m - ctx.start - 10) / 30) + 1;
};
const exitRate = (ctx, m) => {
  const wave = ctx.start + 175;
  if (m < ctx.start) return 0.6;
  // A trickle of early departures, then the main exit wave at the end of the game
  return 2 + 0.02 * (m - ctx.start) + 70 * Math.exp(-Math.pow((m - wave) / 18, 2));
};

const hasQueueSensors = (gate) => !!gate.multiSensor?.queue_time || (gate.queueIds || []).length > 0;

// One gate's queue at a reading, or null while its queue sensor is stale (whole minutes, never below 1, like the backend)
const queueReading = (ctx, sec, i, weight) => {
  const m = Math.floor(sec / 60);
  const c = edgeCase(QUEUE_CASES, i, m);
  if (c === 'stale') return null;
  const p = edgeProgress(m);
  const n = noise(sec / 30 + i * 131 + ctx.seed);
  // Queues build with the entry rush
  const base = 1 + (entryRate(ctx, m) / 100) * 14 * weight * ctx.scale + n * 1.5;
  const v = {
    exact10: 10, nine: 9, spike: 25 + n * 6, minimum: 1, rising: 3 + p * 1.2, falling: 16 - p * 1.2,
  }[c] ?? base;
  return Math.max(1, Math.round(v));
};

// 'get-gates-location-daily-data': { gateId: { totalEntrances, totalExits, data: [{ timestamp, countIn, countOut }], queueData } }
const buildDaily = (currentLocation, dateStr) => {
  const ctx = gameContext(currentLocation.timeZone, dateStr);
  const gates = currentLocation.gates || [];
  const out = {};
  if (!ctx) {
    gates.forEach((g) => { out[String(g.gateId)] = { totalEntrances: 0, totalExits: 0, data: [], queueData: [] }; });
    return out;
  }
  const weights = gates.map((_, i) => gateWeight(i, gates.length));
  const weightSum = weights.reduce((a, b) => a + b, 0) || 1;
  gates.forEach((gate, i) => {
    const share = weights[i] / weightSum;
    let cin = 0;
    let cout = 0;
    const data = [];
    const queueData = [];
    readingTimes(ctx, ctx.gates - 45).forEach((sec, k) => {
      const m = Math.floor(sec / 60);
      const c = edgeCase(FLOW_CASES, i, m);
      const flow = c === 'stopped' ? 0 : c === 'quiet' ? 0.15 : c === 'rush' ? 2.5 : 1;
      const n = noise(sec / 30 + i * 131 + ctx.seed);
      if (k > 0) {
        // Half a minute of people
        cin += Math.round((entryRate(ctx, m) / 2) * ctx.scale * share * flow * (0.7 + n * 0.6));
        cout += Math.round((exitRate(ctx, m) / 2) * ctx.scale * share * (0.6 + noise(sec + i * 17) * 0.8));
      }
      data.push({ timestamp: localStamp(dateStr, sec), countIn: cin, countOut: cout });
      if (hasQueueSensors(gate) && m >= ctx.gates - 10) {
        const q = queueReading(ctx, sec, i, weights[i]);
        if (q != null) queueData.push({ timestamp: localStamp(dateStr, sec), queueTime: q, queueCount: Math.round(q * 9) });
      }
    });
    out[String(gate.gateId)] = { totalEntrances: cin, totalExits: cout, data, queueData };
  });
  return out;
};

// 'get-gates-location-current-data-dashboard' (Locations card): each gate's totals and latest queue
// (0 while its queue sensor is stale, like the backend)
const buildCurrent = (currentLocation) => {
  const date = testSchedule(currentLocation.timeZone)[1].dates[0].date;
  const ctx = gameContext(currentLocation.timeZone, date);
  const daily = buildDaily(currentLocation, date);
  const gates = currentLocation.gates || [];
  const weights = gates.map((_, i) => gateWeight(i, gates.length));
  const sec = ctx.endSec - (ctx.endSec % 30);
  return gates.map((gate, i) => {
    const d = daily[String(gate.gateId)];
    const q = hasQueueSensors(gate) ? queueReading(ctx, sec, i, weights[i]) : null;
    return {
      gateId: gate.gateId,
      totalEntrances: d.totalEntrances,
      totalExits: d.totalExits,
      queueTime: q == null ? 0 : Math.round(q),
      queueCount: q == null ? 0 : Math.round(q * 9),
    };
  });
};

// Drop-in replacement for getQueuesAPI. Serves the fake gates routes, defers everything else.
export const getGatesAPI = (currentLocation) => (route, body, callback, token) => {
  if (!USE_DUMMY_GATES_DATA || !currentLocation) return getQueuesAPI(route, body, callback, token);
  const respond = (data) => setTimeout(() => callback(data), 150);
  switch (route) {
    case 'get-location-current-schedule':
      return respond(testSchedule(currentLocation.timeZone));
    case 'get-gates-location-daily-data':
      return respond(buildDaily(currentLocation, body.date));
    case 'get-gates-location-current-data-dashboard':
      return respond(buildCurrent(currentLocation));
    default:
      return getQueuesAPI(route, body, callback, token);
  }
};
